import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { UserSidebar } from '@/components/layout/UserSidebar'

interface Props {
  user: { name: string; email: string; avatar_url: string | null; github_connected: boolean }
  errors?: string[]
}

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

export default function Edit({ user, errors }: Props) {
  const [name, setName] = useState(user.name)
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url ?? '')

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwErrors, setPwErrors] = useState<string[]>([])
  const [pwSuccess, setPwSuccess] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch('/settings', { name, avatar_url: avatarUrl })
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwErrors([])
    setPwSuccess(false)
    if (newPassword !== pwConfirm) {
      setPwErrors(["New password and confirmation don't match."])
      return
    }
    const res = await fetch('/users/password/change', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({
        user: {
          current_password: currentPassword,
          password: newPassword,
          password_confirmation: pwConfirm,
        },
      }),
    })
    if (res.ok) {
      setPwSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setPwConfirm('')
    } else {
      const data = await res.json().catch(() => ({}))
      setPwErrors(data.errors ?? ['Something went wrong. Please try again.'])
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <UserSidebar active="/settings" />
      <main className="flex-1 p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

        {errors && errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 max-w-lg">
            {errors.map((e, i) => <p key={i} className="text-sm text-red-600">{e}</p>)}
          </div>
        )}

        <div className="max-w-lg space-y-6">
          {/* Profile */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-5">Profile</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Avatar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Avatar</label>
                <div className="flex items-center gap-4 mb-2">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar" className="w-14 h-14 rounded-full object-cover border border-gray-200" />
                  ) : (
                    <span className="w-14 h-14 rounded-full bg-brand text-white text-xl flex items-center justify-center font-bold">
                      {name?.[0]?.toUpperCase() ?? '?'}
                    </span>
                  )}
                  <div className="flex-1">
                    <input
                      value={avatarUrl}
                      onChange={e => setAvatarUrl(e.target.value)}
                      placeholder="https://example.com/avatar.jpg"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono"
                    />
                    <p className="text-xs text-gray-400 mt-1">Paste an image URL or use your GitHub avatar.</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  value={user.email}
                  disabled
                  className="w-full px-4 py-2.5 text-sm border border-gray-100 rounded-xl bg-gray-50 text-gray-400 cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">Email cannot be changed here.</p>
              </div>
              <Button type="submit">Save changes</Button>
            </form>
          </div>

          {/* Change password */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-5">Change password</h2>

            {pwSuccess && (
              <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                Password updated successfully.
              </div>
            )}
            {pwErrors.length > 0 && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {pwErrors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20"
                  placeholder="Min 8 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                <input
                  type="password"
                  value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20"
                  placeholder="••••••••"
                />
              </div>
              <Button type="submit" variant="secondary">Update password</Button>
            </form>
          </div>

          {/* Connected accounts */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Connected accounts</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">GitHub</p>
                  <p className="text-xs text-gray-400">{user.github_connected ? 'Connected' : 'Not connected'}</p>
                </div>
              </div>
              {!user.github_connected && (
                <a href="/users/auth/github" className="text-sm text-brand hover:underline">Connect →</a>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
